/**
 * Market Data Integration Export Options
 *
 * Bloomberg, LSEG, ICE, FactSet, and other market data platform configurations
 */

import type { ExportOptionGroup } from '@/features/data-hub/types/exportConfig.types';

import {
  BloombergIcon,
  DatasetOutlined,
  EnverusIcon,
  FactsetIcon,
  IceIcon,
  LsegIcon,
  VentriksIcon,
  ZemaIcon
} from './icons';
import { integrationButtons, integrationPreview } from './previewConfigs';

export const integrationsGroup: ExportOptionGroup = {
  groupName: 'Market data integrations',
  GroupIcon: DatasetOutlined,
  options: [
    {
      id: 'bloomberg_terminal',
      displayName: 'Bloomberg Terminal',
      icon: { url: BloombergIcon, size: 15 },
      buttonLabel: 'Enable Access',
      label: 'Terminal',
      disabled: false,
      actionHeading: 'GX Data in Bloomberg Terminal',
      bulletPoints: [
        'Fully integrated data available with history at SPOT GX <GO>',
        'Ticker mappings available'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'bloomberg_bpipe',
      displayName: 'Bloomberg B-PIPE',
      icon: { url: BloombergIcon, size: 15 },
      buttonLabel: 'Enable Access',
      label: 'B-PIPE',
      disabled: false,
      actionHeading: 'GX Data in Bloomberg B-PIPE',
      bulletPoints: [
        'Fully integrated data available with history',
        'EID and Ticker mappings available'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'bloomberg_data_license',
      displayName: 'Bloomberg Data License',
      icon: { url: BloombergIcon, size: 15 },
      buttonLabel: 'Enable Access',
      label: 'Data License',
      disabled: false,
      actionHeading: 'GX Data in Bloomberg Data License',
      bulletPoints: [
        'Fully integrated data available with history',
        'EID and Ticker mappings available'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'lseg_workspace',
      displayName: 'LSEG Workspace',
      icon: { url: LsegIcon, size: 16 },
      buttonLabel: 'Enable Access',
      label: 'Workspace',
      disabled: false,
      actionHeading: 'GX Data in LSEG Workspace',
      bulletPoints: [
        'Fully integrated data available with history',
        'Prebuilt screens with key data',
        'RICS mappings available'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'lseg_data_scope',
      displayName: 'LSEG DataScope Select',
      icon: { url: LsegIcon, size: 16 },
      buttonLabel: 'Enable Access',
      label: 'DataScope Select',
      disabled: false,
      actionHeading: 'GX Data in LSEG DataScope Select',
      bulletPoints: ['Fully integrated data available with history', 'RICS mappings available'],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'lseg_ldms',
      displayName: 'LSEG LDMS',
      icon: { url: LsegIcon, size: 16 },
      buttonLabel: 'Enable Access',
      label: 'LDMS',
      disabled: false,
      actionHeading: 'GX Data in LSEG LDMS',
      bulletPoints: ['Fully integrated data available with history', 'RICS mappings available'],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'ice_connect',
      displayName: 'ICE Connect',
      icon: { url: IceIcon, size: 16 },
      buttonLabel: 'Enable Access',
      label: 'Connect',
      disabled: false,
      actionHeading: 'GX Data in ICE Connect',
      bulletPoints: [
        'Fully integrated data available with history',
        'Symbol and Ticker mappings available',
        'GX settled Swaps'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'ice_feed_products',
      displayName: 'ICE Feed Products',
      icon: { url: IceIcon, size: 16 },
      buttonLabel: 'Enable Access',
      label: 'Feed Products',
      disabled: false,
      actionHeading: 'GX Data in ICE Feed Products',
      bulletPoints: [
        'Fully integrated data available with history',
        'Daily and historical tickers available via Consolidated Feed'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },

    {
      id: 'zema',
      displayName: 'Zema',
      icon: { url: ZemaIcon, size: 20 },
      buttonLabel: 'Enable Access',
      disabled: false,
      actionHeading: 'GX Data in Zema',
      bulletPoints: [
        'Fully integrated data available with history',
        'Single GX processor for all data access'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'fact_set',
      displayName: 'FactSet',
      icon: { url: FactsetIcon, size: 16 },
      buttonLabel: 'Enable Access',
      disabled: false,
      actionHeading: 'GX Data in FactSet',
      bulletPoints: ['Fully integrated data available with history'],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'ventriks',
      displayName: 'Ventriks',
      icon: { url: VentriksIcon, size: 12 },
      buttonLabel: 'Enable Access',
      disabled: false,
      actionHeading: 'GX Data in Ventriks',
      bulletPoints: [
        'Fully integrated data available with history',
        'Available via Data Marketplace or Data Platform'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    },
    {
      id: 'enverus',
      displayName: 'Enverus',
      icon: { url: EnverusIcon, size: 30 },
      buttonLabel: 'Enable Access',
      disabled: false,
      actionHeading: 'GX Data in Enverus',
      bulletPoints: [
        'Fully integrated data available with history',
        'Access GX data in Marketview, Sphere & Curvebuilder workflows'
      ],
      buttons: integrationButtons,
      preview: integrationPreview
    }
  ]
};
